// =============================================================================
// services/moderation.ts — Trust & Safety (Apple Guideline 1.2 — UGC) [A15]
//
// The single client surface for REPORT + BLOCK + MUTE across every UGC surface
// (posts, comments, user profiles, direct messages, channel messages). Wraps the
// backend report/block/mute endpoints and maintains a *client-side block/mute
// registry* so that, the instant a user blocks someone, that person's content
// disappears from the reporter's feeds, DMs and notifications — even before the
// next server fetch (Apple requires blocking to take effect immediately).
//
// Backend endpoints wired (base = https://api.cryb.ai/api/v1):
//   POST   /posts/:id/report        { reason(enum), details? }   (A13/A9 posts.ts)
//   POST   /comments/:id/report     { reason(enum), details? }   (A13 comments.ts)
//   POST   /users/:idOrUsername/report   { reason, description? } (A13 users.ts)
//        ^ encodes details:"reported_user:<id>" → A14 admin moderation queue
//   POST   /users/:idOrUsername/block  / DELETE …/block          (A13 users.ts)
//   POST   /users/:idOrUsername/mute   / DELETE …/mute           (A13 users.ts)
//
// MESSAGE / CHANNEL-MESSAGE reports: there is NO message-report endpoint in the
// backend (A14 flagged: the Report model has no message column). We therefore
// report the *sender* via POST /users/:id/report (encodes reported_user:<id> so
// it lands in the admin queue) and pass the message text as the description for
// moderator context. Flagged for A13 to add a true message-report endpoint.
// =============================================================================
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from './api';

// ── Report reason taxonomy ──────────────────────────────────────────────────
// Post/comment endpoints validate this EXACT enum (apps/api posts.ts/comments.ts:
//   spam | harassment | hate_speech | misinformation | violence | nsfw | other).
// The user-report endpoint accepts a free string, so the same codes are reused
// for a consistent picker everywhere.
export type ReportReason =
  | 'spam'
  | 'harassment'
  | 'hate_speech'
  | 'misinformation'
  | 'violence'
  | 'nsfw'
  | 'other';

export const REPORT_REASONS: { code: ReportReason; label: string }[] = [
  { code: 'spam', label: 'Spam or scam' },
  { code: 'harassment', label: 'Harassment or bullying' },
  { code: 'hate_speech', label: 'Hate speech' },
  { code: 'violence', label: 'Violence or threats' },
  { code: 'nsfw', label: 'Nudity or sexual content' },
  { code: 'misinformation', label: 'False information' },
  { code: 'other', label: 'Something else' },
];

export type ReportTargetType = 'post' | 'comment' | 'user' | 'message' | 'channel_message';

export interface ApiResult {
  success: boolean;
  error?: string;
}

// ── Reporting ───────────────────────────────────────────────────────────────

/** Report a post. Lands in the admin moderation queue as a POST report (A14). */
export async function reportPost(postId: string, reason: ReportReason, details?: string): Promise<ApiResult> {
  return api(`/posts/${encodeURIComponent(postId)}/report`, {
    method: 'POST',
    body: JSON.stringify({ reason, details: details || undefined }),
  });
}

/** Report a comment. Lands in the admin queue as a COMMENT report (A14). */
export async function reportComment(commentId: string, reason: ReportReason, details?: string): Promise<ApiResult> {
  return api(`/comments/${encodeURIComponent(commentId)}/report`, {
    method: 'POST',
    body: JSON.stringify({ reason, details: details || undefined }),
  });
}

/**
 * Report a user (profile). Backend encodes details:"reported_user:<id>" so it
 * shows up as a USER report in A14's queue and SUSPEND/BAN can act on it.
 * `idOrUsername` may be either a user id or a @username (backend resolves both).
 */
export async function reportUser(idOrUsername: string, reason: ReportReason, description?: string): Promise<ApiResult> {
  return api(`/users/${encodeURIComponent(idOrUsername)}/report`, {
    method: 'POST',
    body: JSON.stringify({ reason, description: description || undefined }),
  });
}

/**
 * Report a direct/channel message. No dedicated message-report endpoint exists
 * (FLAG:A13), so we report the *sender* and attach the message text + a marker
 * so the moderator sees which message triggered the report in the admin queue.
 */
export async function reportMessage(
  senderIdOrUsername: string,
  reason: ReportReason,
  messageText?: string,
  context: 'message' | 'channel_message' = 'message',
): Promise<ApiResult> {
  const label = context === 'channel_message' ? 'channel message' : 'direct message';
  const quoted = messageText ? `"${messageText.slice(0, 280)}"` : '(no text / attachment)';
  const description = `Reported ${label}: ${quoted}`;
  return reportUser(senderIdOrUsername, reason, description);
}

// ── Block / mute (backend + client-side registry) ───────────────────────────

/**
 * Block a user. Backend create accepts id OR username (`OR:[{id},{username}]`).
 * On success the user is added to the local block registry so their content is
 * hidden from feeds/DMs/notifications immediately (Apple: block is instant).
 */
export async function blockUser(idOrUsername: string, alsoKnownAs?: { id?: string; username?: string }): Promise<ApiResult> {
  const res: ApiResult = await api(`/users/${encodeURIComponent(idOrUsername)}/block`, { method: 'POST' });
  // "already blocked" (400) is effectively a success for the user's intent.
  const ok = res.success || /already blocked/i.test(res.error || '');
  if (ok) {
    await addBlockedLocally(idOrUsername, alsoKnownAs);
  }
  return { success: ok, error: ok ? undefined : res.error };
}

/** Unblock a user (id or username) + remove from the local registry. */
export async function unblockUser(idOrUsername: string): Promise<ApiResult> {
  const res: ApiResult = await api(`/users/${encodeURIComponent(idOrUsername)}/block`, { method: 'DELETE' });
  await removeBlockedLocally(idOrUsername);
  return res;
}

/**
 * Mute a user (id or username). Mutes hide content without breaking follows.
 * On success (or "already muted") the user is added to the local mute registry
 * so their content is hidden from feeds immediately, even before the next fetch.
 */
export async function muteUser(idOrUsername: string, alsoKnownAs?: { id?: string; username?: string }): Promise<ApiResult> {
  const res: ApiResult = await api(`/users/${encodeURIComponent(idOrUsername)}/mute`, { method: 'POST' });
  // "already muted" is effectively a success for the user's intent.
  const ok = res.success || /already muted/i.test(res.error || '');
  if (ok) {
    await addMutedLocally(idOrUsername, alsoKnownAs);
  }
  return { success: ok, error: ok ? undefined : res.error };
}

/** Unmute a user (id or username) + remove from the local registry. */
export async function unmuteUser(idOrUsername: string): Promise<ApiResult> {
  const res: ApiResult = await api(`/users/${encodeURIComponent(idOrUsername)}/mute`, { method: 'DELETE' });
  await removeMutedLocally(idOrUsername);
  return res;
}

// ── Client-side block registry ──────────────────────────────────────────────
// A device-local set of blocked identifiers (both ids and usernames, lowercased)
// so any surface can instantly hide a blocked user's content without waiting for
// a server round-trip. Backed by AsyncStorage; notifies subscribers on change.

const BLOCK_KEY = 'cryb_blocked_users_v1';
let _blocked: Set<string> = new Set();
let _loaded = false;
const _listeners = new Set<() => void>();

function norm(v?: string | null): string {
  return (v || '').trim().toLowerCase().replace(/^@/, '');
}

function emit(): void {
  for (const fn of _listeners) {
    try { fn(); } catch { /* a bad listener never breaks the rest */ }
  }
}

/** Load the persisted block set once (idempotent). Call early (e.g. on app boot). */
export async function loadBlockedUsers(): Promise<void> {
  if (_loaded) return;
  try {
    const raw = await AsyncStorage.getItem(BLOCK_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) _blocked = new Set(parsed.map((x) => String(x)).filter(Boolean));
    }
  } catch { /* ignore — empty set is a safe default */ }
  _loaded = true;
  emit();
}

async function persist(): Promise<void> {
  try { await AsyncStorage.setItem(BLOCK_KEY, JSON.stringify(Array.from(_blocked))); } catch { /* non-fatal */ }
}

async function addBlockedLocally(idOrUsername: string, aka?: { id?: string; username?: string }): Promise<void> {
  for (const v of [idOrUsername, aka?.id, aka?.username]) {
    const n = norm(v);
    if (n) _blocked.add(n);
  }
  await persist();
  emit();
}

async function removeBlockedLocally(idOrUsername: string): Promise<void> {
  _blocked.delete(norm(idOrUsername));
  await persist();
  emit();
}

/**
 * Replace the entire local block set from the server's blocked list (ids +
 * usernames). Call after GET /settings/blocked so the device matches the server.
 */
export async function syncBlockedUsers(entries: Array<{ id?: string; username?: string }>): Promise<void> {
  const next = new Set<string>();
  for (const e of entries) {
    const i = norm(e?.id); if (i) next.add(i);
    const u = norm(e?.username); if (u) next.add(u);
  }
  _blocked = next;
  _loaded = true;
  await persist();
  emit();
}

/**
 * LR4: apply a SERVER-originated block change (from a `block:update` socket
 * event) to the local registry. Used for the REVERSE direction — when someone
 * blocks ME, my device must hide their content live even though *I* never called
 * blockUser. `active` true ⇒ add, false ⇒ remove. Mirrors the local add/remove
 * paths so feeds re-filter via onBlockedChange. Never throws.
 */
export async function applyServerBlock(
  who: { id?: string; username?: string },
  active: boolean
): Promise<void> {
  try {
    if (active) {
      await addBlockedLocally(who.id || who.username || '', { id: who.id, username: who.username });
    } else {
      // Remove every key this user is known by.
      for (const v of [who.id, who.username]) {
        const n = norm(v);
        if (n) _blocked.delete(n);
      }
      await persist();
      emit();
    }
  } catch { /* non-fatal */ }
}

/** True if any of the given identifiers (id / username) is blocked. */
export function isBlocked(...identifiers: Array<string | null | undefined>): boolean {
  for (const v of identifiers) {
    const n = norm(v);
    if (n && _blocked.has(n)) return true;
  }
  return false;
}

/** Subscribe to block-set changes (e.g. to re-filter a feed). Returns unsubscribe. */
export function onBlockedChange(fn: () => void): () => void {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/** Current count of locally-blocked identifiers (ids+usernames may double-count). */
export function blockedSize(): number {
  return _blocked.size;
}

// ── Client-side mute registry ───────────────────────────────────────────────
// Mirror of the block registry above, for MUTE. A device-local set of muted
// identifiers (both ids and usernames, lowercased) so any surface can instantly
// hide a muted user's content without waiting for a server round-trip. Backed by
// AsyncStorage; notifies subscribers on change.

const MUTE_KEY = 'cryb.moderation.muted';
let _muted: Set<string> = new Set();
let _mutedLoaded = false;
const _mutedListeners = new Set<() => void>();

function emitMuted(): void {
  for (const fn of _mutedListeners) {
    try { fn(); } catch { /* a bad listener never breaks the rest */ }
  }
}

/** Load the persisted mute set once (idempotent). Call early (e.g. on app boot). */
export async function loadMutedUsers(): Promise<void> {
  if (_mutedLoaded) return;
  try {
    const raw = await AsyncStorage.getItem(MUTE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) _muted = new Set(parsed.map((x) => String(x)).filter(Boolean));
    }
  } catch { /* ignore — empty set is a safe default */ }
  _mutedLoaded = true;
  emitMuted();
}

async function persistMuted(): Promise<void> {
  try { await AsyncStorage.setItem(MUTE_KEY, JSON.stringify(Array.from(_muted))); } catch { /* non-fatal */ }
}

async function addMutedLocally(idOrUsername: string, aka?: { id?: string; username?: string }): Promise<void> {
  for (const v of [idOrUsername, aka?.id, aka?.username]) {
    const n = norm(v);
    if (n) _muted.add(n);
  }
  await persistMuted();
  emitMuted();
}

async function removeMutedLocally(idOrUsername: string): Promise<void> {
  _muted.delete(norm(idOrUsername));
  await persistMuted();
  emitMuted();
}

/**
 * Replace the entire local mute set from the server's muted list (ids +
 * usernames). Call after fetching the server-side muted list so the device
 * matches the server.
 */
export async function syncMutedUsers(entries: Array<{ id?: string; username?: string }>): Promise<void> {
  const next = new Set<string>();
  for (const e of entries) {
    const i = norm(e?.id); if (i) next.add(i);
    const u = norm(e?.username); if (u) next.add(u);
  }
  _muted = next;
  _mutedLoaded = true;
  await persistMuted();
  emitMuted();
}

/** True if any of the given identifiers (id / username) is muted. */
export function isMuted(...identifiers: Array<string | null | undefined>): boolean {
  for (const v of identifiers) {
    const n = norm(v);
    if (n && _muted.has(n)) return true;
  }
  return false;
}

/** Subscribe to mute-set changes (e.g. to re-filter a feed). Returns unsubscribe. */
export function onMutedChange(fn: () => void): () => void {
  _mutedListeners.add(fn);
  return () => _mutedListeners.delete(fn);
}

/** Current count of locally-muted identifiers (ids+usernames may double-count). */
export function mutedSize(): number {
  return _muted.size;
}
